import React, { useState, useEffect } from 'react'
import * as api from '../../services/api.js'
import { todayISO } from '../../utils/dates.js'
import AppSelect from '../../components/AppSelect.jsx'

const FIELD_TYPE_OPTIONS = [
  { value: 'text',    label: 'Text' },
  { value: 'number',  label: 'Number' },
  { value: 'select',  label: 'Select' },
  { value: 'boolean', label: 'Toggle' },
]

// ── Constants ─────────────────────────────────────────────────────────────────

const TRACKER_COLORS = ['#e8f5ed','#ede8f5','#e8f0f5','#f5f0e8','#fde8e8','#f5f0e0']

const EMOJI_OPTIONS = ['📊','🌙','💧','📖','✨','💪','🚶','🧘','🍎','💊','🏃','🎯','📝','⭐','🌿','🎨','🎵','💤','🔥','🌸']

const ALL_DAYS = ['mon','tue','wed','thu','fri','sat','sun']
const DAY_LABELS = { mon: 'M', tue: 'T', wed: 'W', thu: 'T', fri: 'F', sat: 'S', sun: 'S' }
const DAY_FULL   = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' }

const TRACKER_TEMPLATES = [
  { title: 'Morning walk',       icon: '🚶', color: '#e8f5ed', tracker_type: 'habit',   config: { target_days: ALL_DAYS },                         description: 'Did you go for a walk today?' },
  { title: 'Sleep hours',        icon: '🌙', color: '#ede8f5', tracker_type: 'numeric', config: { unit: 'hours', goal: 8 },                        description: 'Log how many hours you sleep' },
  { title: 'Water intake',       icon: '💧', color: '#e8f0f5', tracker_type: 'numeric', config: { unit: 'glasses', goal: 8 },                      description: 'Count your glasses of water' },
  { title: 'Read before bed',    icon: '📖', color: '#f5f0e8', tracker_type: 'habit',   config: { target_days: ALL_DAYS },                         description: 'Did you read before sleeping?' },
  { title: 'Daily mood & energy',icon: '✨', color: '#fde8e8', tracker_type: 'custom',  config: { fields: [{ label: 'Mood', type: 'select', options: ['Low','Okay','Good','Great'] }, { label: 'Energy', type: 'select', options: ['Low','Medium','High'] }, { label: 'Notes', type: 'text' }] }, description: 'A quick daily check-in' },
  { title: 'Workout log',        icon: '💪', color: '#e8f5ed', tracker_type: 'custom',  config: { fields: [{ label: 'Activity', type: 'text' }, { label: 'Duration', type: 'number' }, { label: 'Felt', type: 'select', options: ['Hard','Okay','Good','Great'] }] }, description: 'Log your workouts' },
]

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseJSON(raw, fallback = {}) {
  if (typeof raw === 'object' && raw !== null) return raw
  try { return JSON.parse(raw || JSON.stringify(fallback)) } catch { return fallback }
}

function calcStreak(entries) {
  if (!entries.length) return 0
  const dates = [...new Set(entries.map(e => e.date))].sort().reverse()
  const today = todayISO()
  let streak = 0
  let expected = today
  for (const d of dates) {
    if (d === expected) {
      streak++
      const dt = new Date(expected + 'T12:00:00')
      dt.setDate(dt.getDate() - 1)
      expected = dt.toISOString().slice(0, 10)
    } else if (d < expected) {
      break
    }
  }
  return streak
}

function formatDate(iso) {
  const d = new Date(iso + 'T12:00:00')
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

function getDayOfWeek(iso) {
  const d = new Date(iso + 'T12:00:00')
  return ['sun','mon','tue','wed','thu','fri','sat'][d.getDay()]
}

function getLast30Days() {
  const days = []
  for (let i = 29; i >= 0; i--) {
    const d = new Date(Date.now() - i * 86400000)
    days.push(d.toISOString().slice(0, 10))
  }
  return days
}

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate()
}

function getFirstDayOfWeek(year, month) {
  const dow = new Date(year, month, 1).getDay()
  return dow === 0 ? 6 : dow - 1 // Mon = 0
}

// ── Small reusable UI ─────────────────────────────────────────────────────────

function EmojiPicker({ value, onSelect }) {
  const [open, setOpen] = useState(false)
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        style={{ fontSize: '1.4rem', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px', borderRadius: 8 }}
        onClick={() => setOpen(v => !v)}
      >
        {value}
      </button>
      {open && (
        <div style={{ position: 'absolute', zIndex: 300, top: '100%', left: 0, background: 'var(--card-bg, var(--surf))', border: '0.5px solid rgba(0,0,0,0.12)', borderRadius: 12, padding: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 3, minWidth: 160 }}>
          {EMOJI_OPTIONS.map(e => (
            <button key={e} type="button" style={{ fontSize: '1.1rem', padding: 4, borderRadius: 6, background: 'none', border: 'none', cursor: 'pointer' }}
              onClick={() => { onSelect(e); setOpen(false) }}
            >{e}</button>
          ))}
        </div>
      )}
    </div>
  )
}

function ColorRow({ value, onSelect }) {
  return (
    <div className="color-row">
      {TRACKER_COLORS.map(c => (
        <div
          key={c}
          className={`color-chip${value === c ? ' selected' : ''}`}
          style={{ background: c }}
          onClick={() => onSelect(c)}
        />
      ))}
    </div>
  )
}

// ── Habit calendar ────────────────────────────────────────────────────────────

function HabitCalendar({ entries, targetDays }) {
  const today = todayISO()
  const now   = new Date(today + 'T12:00:00')
  const year  = now.getFullYear()
  const month = now.getMonth()
  const daysInMonth = getDaysInMonth(year, month)
  const firstDow    = getFirstDayOfWeek(year, month)

  const loggedSet = new Set(entries.map(e => e.date))
  const target    = targetDays || ALL_DAYS

  const cells = []
  for (let i = 0; i < firstDow; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) {
    const iso  = `${year}-${String(month + 1).padStart(2,'0')}-${String(d).padStart(2,'0')}`
    const dow  = getDayOfWeek(iso)
    const isTarget  = target.includes(dow)
    const isDone    = loggedSet.has(iso)
    const isFuture  = iso > today
    const isToday   = iso === today
    cells.push({ iso, d, isTarget, isDone, isFuture, isToday })
  }

  const doneThisMonth   = entries.filter(e => e.date.startsWith(`${year}-${String(month+1).padStart(2,'0')}`)).length
  const targetThisMonth = cells.filter(c => c && c.isTarget && !c.isFuture).length
  const pct = targetThisMonth > 0 ? Math.round((doneThisMonth / targetThisMonth) * 100) : 0

  return (
    <div className="habit-calendar-wrap">
      <div className="habit-cal-header">
        {ALL_DAYS.map(d => <div key={d} className="habit-cal-dow">{DAY_LABELS[d]}</div>)}
      </div>
      <div className="habit-calendar">
        {cells.map((cell, i) => {
          if (!cell) return <div key={`empty-${i}`} />
          let cls = 'habit-day'
          if (cell.isFuture)       cls += ' future'
          else if (!cell.isTarget) cls += ' skipped'
          else if (cell.isDone)    cls += ' done'
          else                     cls += ' missed'
          if (cell.isToday) cls += ' today'
          return <div key={cell.iso} className={cls} title={cell.iso}>{cell.d}</div>
        })}
      </div>
      <div style={{ marginTop: 8, fontSize: '0.8rem', color: 'var(--stone)' }}>
        Completion this month: <strong style={{ color: 'var(--accent)' }}>{pct}%</strong>
        {' '}({doneThisMonth} / {targetThisMonth} days)
      </div>
    </div>
  )
}

// ── Numeric chart ─────────────────────────────────────────────────────────────

function NumericChart({ entries, goal, unit }) {
  const days   = getLast30Days()
  const today  = todayISO()
  const byDate = {}
  entries.forEach(e => {
    const v = parseJSON(e.value, {})
    if (v.value != null) byDate[e.date] = Number(v.value)
  })

  const values = days.map(d => byDate[d] ?? null)
  const max    = Math.max(...values.filter(v => v !== null), goal || 0, 1)

  const daysWithValue = values.filter(v => v !== null)
  const avg    = daysWithValue.length ? (daysWithValue.reduce((s, v) => s + v, 0) / daysWithValue.length).toFixed(1) : '—'
  const high   = daysWithValue.length ? Math.max(...daysWithValue) : '—'
  const low    = daysWithValue.length ? Math.min(...daysWithValue) : '—'

  return (
    <div>
      <div className="numeric-stats-row">
        <div className="num-stat"><div className="num-stat-val">{avg}</div><div className="num-stat-lbl">Avg {unit || ''}</div></div>
        <div className="num-stat"><div className="num-stat-val">{high}</div><div className="num-stat-lbl">Highest</div></div>
        <div className="num-stat"><div className="num-stat-val">{low}</div><div className="num-stat-lbl">Lowest</div></div>
      </div>
      <div className="numeric-chart-wrap" style={{ position: 'relative' }}>
        {goal && max > 0 && (
          <div
            className="goal-line"
            style={{ bottom: `${(goal / max) * 100}%` }}
          />
        )}
        {days.map((d, i) => {
          const v   = values[i]
          const h   = v !== null ? Math.max(Math.round((v / max) * 100), 2) : 0
          let cls   = 'numeric-bar'
          if (v !== null) cls += goal && v >= goal ? ' above-goal' : ' has-value'
          if (d === today) cls += ' today-bar'
          return (
            <div
              key={d}
              className={cls}
              style={{ height: `${h}%` }}
              title={v !== null ? `${d}: ${v} ${unit || ''}` : d}
            />
          )
        })}
      </div>
      <div style={{ fontSize: '0.68rem', color: 'var(--stone)', textAlign: 'right' }}>
        Last 30 days{goal ? ` · Goal: ${goal} ${unit || ''}` : ''}
      </div>
    </div>
  )
}

// ── Custom tracker table ──────────────────────────────────────────────────────

function CustomTrackerTable({ entries, fields }) {
  if (!entries.length) return (
    <p style={{ color: 'var(--stone)', fontStyle: 'italic', fontSize: '0.85rem' }}>No entries yet.</p>
  )

  const recent = entries.slice(0, 30)
  const today  = todayISO()

  // Frequency summaries for select fields
  const selectFields = (fields || []).filter(f => f.type === 'select')
  const freqSummaries = selectFields.map(f => {
    const counts = {}
    entries.forEach(e => {
      const v = parseJSON(e.value, {})
      const val = v[f.label]
      if (val) counts[val] = (counts[val] || 0) + 1
    })
    return { label: f.label, counts }
  })

  function formatEntryDate(d) {
    if (d === today) return 'Today'
    const diff = Math.floor((new Date(today + 'T12:00:00') - new Date(d + 'T12:00:00')) / 86400000)
    if (diff === 1) return 'Yesterday'
    if (diff < 7)  return `${diff}d ago`
    return formatDate(d)
  }

  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table className="custom-tracker-table">
          <thead>
            <tr>
              <th>Date</th>
              {(fields || []).map(f => <th key={f.label}>{f.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {recent.map(e => {
              const v = parseJSON(e.value, {})
              return (
                <tr key={e.id}>
                  <td>{formatEntryDate(e.date)}</td>
                  {(fields || []).map(f => <td key={f.label}>{v[f.label] ?? '—'}</td>)}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {freqSummaries.map(({ label, counts }) => (
        <div key={label} className="freq-summary">
          <div className="freq-summary-title">{label} this month</div>
          <div className="freq-row">
            {Object.entries(counts).map(([opt, cnt]) => (
              <span key={opt} className="freq-chip">{opt}: {cnt}</span>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Log entry panels ──────────────────────────────────────────────────────────

function HabitLogPanel({ tracker, entries, onLogged, onUndo }) {
  const today    = todayISO()
  const todayEntry = entries.find(e => e.date === today)
  const isDone   = todayEntry && parseJSON(todayEntry.value, {}).done === true

  async function log() {
    await api.logTrackerEntry({ tracker_id: tracker.id, date: today, value: { done: true } })
    onLogged()
  }

  async function undo() {
    if (todayEntry) {
      await api.deleteTrackerEntry(todayEntry.id, tracker.id)
      onUndo()
    }
  }

  if (isDone) {
    return (
      <div className="ms-log-panel">
        <p style={{ color: 'var(--accent)', fontWeight: 500, fontSize: '0.95rem', marginBottom: '0.75rem' }}>
          ✓ Logged for today
        </p>
        <button className="ghost-btn" style={{ fontSize: '0.82rem' }} onClick={undo}>
          Undo
        </button>
      </div>
    )
  }

  return (
    <div className="ms-log-panel">
      <h3>Log today</h3>
      <button className="save-btn ready" style={{ fontSize: '0.88rem' }} onClick={log}>
        Done today ✓
      </button>
    </div>
  )
}

function NumericLogPanel({ tracker, entries, onLogged }) {
  const config     = parseJSON(tracker.config, {})
  const today      = todayISO()
  const todayEntry = entries.find(e => e.date === today)
  const existing   = todayEntry ? parseJSON(todayEntry.value, {}).value : null
  const [val, setVal] = useState(existing != null ? String(existing) : '')
  const [editing, setEditing] = useState(!todayEntry)

  async function save() {
    const num = parseFloat(val)
    if (isNaN(num)) return
    await api.logTrackerEntry({ tracker_id: tracker.id, date: today, value: { value: num } })
    setEditing(false)
    onLogged()
  }

  if (!editing && existing != null) {
    return (
      <div className="ms-log-panel">
        <h3>Today's log</h3>
        <p style={{ fontSize: '1.1rem', color: 'var(--bark)', marginBottom: '0.75rem' }}>
          <strong>{existing}</strong> {config.unit || ''}
          {config.goal ? <span style={{ color: 'var(--stone)', fontSize: '0.8rem' }}> / {config.goal} goal</span> : null}
        </p>
        <button className="ghost-btn" style={{ fontSize: '0.82rem' }} onClick={() => setEditing(true)}>
          Edit
        </button>
      </div>
    )
  }

  return (
    <div className="ms-log-panel">
      <h3>Log today</h3>
      <div className="log-num-row">
        <input
          type="number"
          className="log-num-input"
          value={val}
          step="0.1"
          onChange={e => setVal(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && save()}
          placeholder="0"
          autoFocus
        />
        <span className="log-unit-label">{config.unit || 'units'}</span>
      </div>
      {config.goal && (
        <p style={{ fontSize: '0.78rem', color: 'var(--stone)', marginBottom: '0.75rem' }}>
          Daily goal: {config.goal} {config.unit || ''}
        </p>
      )}
      <button
        className={`save-btn${val.trim() ? ' ready' : ''}`}
        style={{ fontSize: '0.82rem' }}
        onClick={save}
        disabled={!val.trim()}
      >
        Save
      </button>
    </div>
  )
}

function CustomLogPanel({ tracker, entries, onLogged }) {
  const config     = parseJSON(tracker.config, {})
  const fields     = config.fields || []
  const today      = todayISO()
  const todayEntry = entries.find(e => e.date === today)
  const existing   = todayEntry ? parseJSON(todayEntry.value, {}) : null
  const [formData, setFormData] = useState(existing || {})
  const [saved, setSaved] = useState(!!existing)

  function setField(label, value) {
    setFormData(prev => ({ ...prev, [label]: value }))
    setSaved(false)
  }

  async function save() {
    await api.logTrackerEntry({ tracker_id: tracker.id, date: today, value: formData })
    setSaved(true)
    onLogged()
  }

  return (
    <div className="ms-log-panel">
      <h3>Log today</h3>
      <div className="custom-log-form">
        {fields.map(field => (
          <div key={field.label} className="custom-log-field">
            <label>{field.label}</label>
            {field.type === 'select' ? (
              <select value={formData[field.label] || ''} onChange={e => setField(field.label, e.target.value)}>
                <option value="">— Choose —</option>
                {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : field.type === 'boolean' ? (
              <div className="toggle-field">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={!!formData[field.label]}
                    onChange={e => setField(field.label, e.target.checked)}
                  />
                  <span className="toggle-slider" />
                </label>
                <span style={{ fontSize: '0.85rem', color: 'var(--bark)' }}>
                  {formData[field.label] ? 'Yes' : 'No'}
                </span>
              </div>
            ) : (
              <input
                type={field.type === 'number' ? 'number' : 'text'}
                value={formData[field.label] || ''}
                onChange={e => setField(field.label, e.target.value)}
                placeholder={field.label}
              />
            )}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: '0.75rem' }}>
        <button className="save-btn ready" style={{ fontSize: '0.82rem' }} onClick={save}>Save</button>
        {saved && <span style={{ fontSize: '0.78rem', color: 'var(--accent)', fontStyle: 'italic' }}>Saved ✓</span>}
      </div>
    </div>
  )
}

// ── Tracker detail view ───────────────────────────────────────────────────────

function TrackerDetail({ tracker, onBack }) {
  const [entries, setEntries] = useState([])
  const config = parseJSON(tracker.config, {})

  useEffect(() => {
    const from = getLast30Days()[0]
    const to   = todayISO()
    api.getTrackerEntries(tracker.id, from, to).then(d => setEntries(d || []))
  }, [tracker.id])

  function reload() {
    const from = getLast30Days()[0]
    const to   = todayISO()
    api.getTrackerEntries(tracker.id, from, to).then(d => setEntries(d || []))
  }

  const streak      = calcStreak(entries)
  const longestStreak = (() => {
    if (!entries.length) return 0
    const dates = [...new Set(entries.map(e => e.date))].sort()
    let longest = 1, current = 1
    for (let i = 1; i < dates.length; i++) {
      const prev = new Date(dates[i-1] + 'T12:00:00')
      const curr = new Date(dates[i]   + 'T12:00:00')
      const diff = Math.round((curr - prev) / 86400000)
      if (diff === 1) { current++; longest = Math.max(longest, current) }
      else { current = 1 }
    }
    return longest
  })()

  return (
    <div>
      <button className="tracker-detail-back" onClick={onBack}>← Back</button>
      <div className="tracker-detail-header">
        <span className="tracker-detail-icon">{tracker.icon || '📊'}</span>
        <div>
          <div className="tracker-detail-title">{tracker.title}</div>
          <div className="tracker-detail-type">{tracker.tracker_type} tracker</div>
        </div>
      </div>

      {/* Log entry */}
      {tracker.tracker_type === 'habit' && (
        <HabitLogPanel tracker={tracker} entries={entries} onLogged={reload} onUndo={reload} />
      )}
      {tracker.tracker_type === 'numeric' && (
        <NumericLogPanel tracker={tracker} entries={entries} onLogged={reload} />
      )}
      {tracker.tracker_type === 'custom' && (
        <CustomLogPanel tracker={tracker} entries={entries} onLogged={reload} />
      )}

      {/* Stats */}
      <div className="tracker-streak-stats">
        <div className="ts-stat">
          <div className="ts-val">{streak}</div>
          <div className="ts-lbl">Current streak</div>
        </div>
        <div className="ts-stat">
          <div className="ts-val">{longestStreak}</div>
          <div className="ts-lbl">Longest streak</div>
        </div>
        <div className="ts-stat">
          <div className="ts-val">{entries.length}</div>
          <div className="ts-lbl">Total entries</div>
        </div>
      </div>

      {/* Type-specific detail view */}
      {tracker.tracker_type === 'habit' && (
        <HabitCalendar entries={entries} targetDays={config.target_days} />
      )}
      {tracker.tracker_type === 'numeric' && (
        <NumericChart entries={entries} goal={config.goal} unit={config.unit} />
      )}
      {tracker.tracker_type === 'custom' && (
        <CustomTrackerTable entries={entries} fields={config.fields} />
      )}
    </div>
  )
}

// ── Create tracker panel ──────────────────────────────────────────────────────

function CreateTrackerPanel({ onCreated, onCancel }) {
  const [type, setType] = useState('habit')
  const [form, setForm] = useState({ title: '', icon: '📊', color: '#e8f5ed', description: '' })
  const [habitDays, setHabitDays] = useState([...ALL_DAYS])
  const [numUnit, setNumUnit] = useState('')
  const [numGoal, setNumGoal] = useState('')
  const [customFields, setCustomFields] = useState([{ label: '', type: 'text', options: '' }])

  function setF(k, v) { setForm(prev => ({ ...prev, [k]: v })) }

  function toggleDay(day) {
    setHabitDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day])
  }

  function addField() {
    setCustomFields(prev => [...prev, { label: '', type: 'text', options: '' }])
  }

  function updateField(i, k, v) {
    setCustomFields(prev => prev.map((f, idx) => idx === i ? { ...f, [k]: v } : f))
  }

  function removeField(i) {
    setCustomFields(prev => prev.filter((_, idx) => idx !== i))
  }

  function buildConfig() {
    if (type === 'habit')   return { target_days: habitDays }
    if (type === 'numeric') return { unit: numUnit, goal: numGoal ? parseFloat(numGoal) : null }
    if (type === 'custom')  return {
      fields: customFields
        .filter(f => f.label.trim())
        .map(f => ({
          label:   f.label.trim(),
          type:    f.type,
          options: f.type === 'select' ? f.options.split(',').map(o => o.trim()).filter(Boolean) : undefined,
        }))
    }
    return {}
  }

  async function handleCreate() {
    if (!form.title.trim()) return
    const res = await api.createMyTracker({
      title:        form.title.trim(),
      description:  form.description || null,
      tracker_type: type,
      icon:         form.icon,
      color:        form.color,
      config:       buildConfig(),
    })
    if (res?.id) {
      onCreated({ id: res.id, title: form.title.trim(), tracker_type: type, icon: form.icon, color: form.color, config: buildConfig(), description: form.description || null })
    }
  }

  return (
    <div className="ms-create-panel">
      <h3>Create tracker</h3>

      {/* Type */}
      <div className="tracker-type-select">
        {[{id:'habit',label:'Habit'},{id:'numeric',label:'Numeric'},{id:'custom',label:'Custom'}].map(t => (
          <button key={t.id} className={`tracker-type-btn${type === t.id ? ' active' : ''}`} onClick={() => setType(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="tracker-field-grid">
        <div className="full">
          <label className="field-label">Title</label>
          <input
            className="field-input"
            placeholder={type === 'habit' ? 'e.g. Morning walk' : type === 'numeric' ? 'e.g. Sleep hours' : 'e.g. Daily check-in'}
            value={form.title}
            onChange={e => setF('title', e.target.value)}
          />
        </div>
        <div>
          <label className="field-label">Icon</label>
          <EmojiPicker value={form.icon} onSelect={v => setF('icon', v)} />
        </div>
        <div>
          <label className="field-label">Color</label>
          <ColorRow value={form.color} onSelect={v => setF('color', v)} />
        </div>
      </div>

      {/* Habit-specific */}
      {type === 'habit' && (
        <div style={{ marginBottom: '1rem' }}>
          <label className="field-label" style={{ display: 'block', marginBottom: 6 }}>Target days</label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {ALL_DAYS.map(d => (
              <button
                key={d}
                type="button"
                style={{
                  width: 36, height: 36, borderRadius: '50%', border: '1px solid rgba(0,0,0,0.15)',
                  background: habitDays.includes(d) ? 'var(--accent)' : 'transparent',
                  color: habitDays.includes(d) ? 'white' : 'var(--bark)',
                  cursor: 'pointer', fontSize: '0.75rem', fontWeight: 500,
                }}
                onClick={() => toggleDay(d)}
              >
                {DAY_LABELS[d]}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Numeric-specific */}
      {type === 'numeric' && (
        <div className="tracker-field-grid">
          <div>
            <label className="field-label">Unit</label>
            <input className="field-input" placeholder="e.g. hours, glasses, km" value={numUnit} onChange={e => setNumUnit(e.target.value)} />
          </div>
          <div>
            <label className="field-label">Daily goal (optional)</label>
            <input className="field-input" type="number" placeholder="e.g. 8" value={numGoal} onChange={e => setNumGoal(e.target.value)} />
          </div>
        </div>
      )}

      {/* Custom fields */}
      {type === 'custom' && (
        <div className="field-builder">
          <label className="field-label" style={{ display: 'block', marginBottom: 6 }}>Fields (up to 8)</label>
          {customFields.map((field, i) => (
            <div key={i} className="field-builder-row">
              <input className="fb-label" placeholder="Field name" value={field.label} onChange={e => updateField(i, 'label', e.target.value)} />
              <AppSelect
                className="fb-type"
                value={field.type}
                onChange={v => updateField(i, 'type', v)}
                options={FIELD_TYPE_OPTIONS}
              />
              {field.type === 'select' && (
                <input className="fb-opts" placeholder="A, B, C" value={field.options} onChange={e => updateField(i, 'options', e.target.value)} />
              )}
              <button className="fb-del" onClick={() => removeField(i)}>✕</button>
            </div>
          ))}
          {customFields.length < 8 && (
            <button className="ghost-btn" style={{ fontSize: '0.78rem', marginTop: 6 }} onClick={addField}>
              + Add field
            </button>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: '1.25rem' }}>
        <button
          className={`save-btn${form.title.trim() ? ' ready' : ''}`}
          onClick={handleCreate}
          disabled={!form.title.trim()}
        >
          Create tracker
        </button>
        <button className="ghost-btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  )
}

// ── Tracker card ──────────────────────────────────────────────────────────────

function TrackerCard({ tracker, todayEntry, onLog, onDetail, onDelete }) {
  const config   = parseJSON(tracker.config, {})
  const v        = todayEntry ? parseJSON(todayEntry.value, {}) : null
  const isHabit  = tracker.tracker_type === 'habit'
  const isNum    = tracker.tracker_type === 'numeric'

  let todayStatus = null
  let statusClass = ''
  if (isHabit) {
    todayStatus = v?.done ? '✓ Done' : '○ Not yet'
    statusClass = v?.done ? ' done' : ''
  } else if (isNum) {
    todayStatus = v?.value != null ? `${v.value} ${config.unit || ''}` : '— not logged'
  } else {
    todayStatus = v ? 'Logged' : 'Not logged'
  }

  return (
    <div
      className="ms-tracker-card"
      style={{ borderLeftColor: tracker.color || '#e8f0f5' }}
      onClick={() => onDetail(tracker)}
    >
      <div className="tracker-title-row">
        <span className="tracker-icon">{tracker.icon || '📊'}</span>
        <span className="tracker-title">{tracker.title}</span>
        <span className="tracker-type-badge">{tracker.tracker_type}</span>
      </div>

      <div className={`tracker-today-status${statusClass}`}>{todayStatus}</div>

      <div className="tracker-card-actions" onClick={e => e.stopPropagation()}>
        <button
          className="save-btn ready"
          style={{ fontSize: '0.75rem', padding: '5px 12px' }}
          onClick={() => onLog(tracker)}
        >
          Log today
        </button>
        <button
          className="ghost-btn"
          style={{ fontSize: '0.72rem', marginLeft: 'auto', color: 'var(--stone)' }}
          onClick={() => onDelete(tracker)}
        >
          ✕
        </button>
      </div>
    </div>
  )
}

// ── Template picker modal ─────────────────────────────────────────────────────

function TrackerTemplatePicker({ onClose, onCreated }) {
  const [loading, setLoading] = useState(false)

  async function useTemplate(tmpl) {
    if (loading) return
    setLoading(true)
    try {
      const res = await api.createMyTracker({
        title:        tmpl.title,
        description:  tmpl.description,
        tracker_type: tmpl.tracker_type,
        icon:         tmpl.icon,
        color:        tmpl.color,
        config:       tmpl.config,
      })
      if (res?.id) {
        onCreated({ id: res.id, ...tmpl })
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <button className="page-editor-back" onClick={onClose}>← Back</button>
      <h2 style={{ fontFamily: 'var(--font-serif)', fontWeight: 400, marginBottom: '1rem', color: 'var(--ink)', fontSize: '1.2rem' }}>
        Tracker templates
      </h2>
      <div className="template-grid">
        {TRACKER_TEMPLATES.map((tmpl, i) => (
          <div key={i} className="template-card">
            <div className="tc-cover" style={{ background: tmpl.color }} />
            <div className="tc-body">
              <div className="tc-title">{tmpl.icon} {tmpl.title}</div>
              <div className="tc-desc">{tmpl.description}</div>
              <div style={{ fontSize: '0.68rem', color: 'var(--stone)', marginBottom: 8 }}>{tmpl.tracker_type}</div>
              <button
                className="save-btn ready"
                style={{ fontSize: '0.78rem', padding: '5px 12px' }}
                disabled={loading}
                onClick={() => useTemplate(tmpl)}
              >
                Use this
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── MyTrackers main ───────────────────────────────────────────────────────────

export default function MyTrackers({ layoutClass }) {
  const [trackers, setTrackers]       = useState([])
  const [todayEntries, setTodayEntries] = useState({})
  const [view, setView]               = useState('list') // 'list' | 'create' | 'templates' | 'detail' | 'log'
  const [activeTracker, setActiveTracker] = useState(null)
  const [loading, setLoading]         = useState(true)
  const [confirmDelete, setConfirmDelete] = useState(null)

  const today = todayISO()

  useEffect(() => {
    api.getMyTrackers().then(rows => {
      const ts = rows || []
      setTrackers(ts)
      setLoading(false)
      loadTodayEntries(ts)
    })
  }, [])

  async function loadTodayEntries(ts) {
    const all = await Promise.all((ts || trackers).map(t => api.getTrackerEntries(t.id, today, today)))
    const map = {}
    ;(ts || trackers).forEach((t, i) => {
      const entry = (all[i] || [])[0] || null
      map[t.id] = entry
    })
    setTodayEntries(map)
  }

  function handleTrackerCreated(tracker) {
    setTrackers(prev => [tracker, ...prev])
    setView('detail')
    setActiveTracker(tracker)
  }

  async function handleDelete(tracker) {
    await api.deleteMyTracker(tracker.id)
    setTrackers(prev => prev.filter(t => t.id !== tracker.id))
    setConfirmDelete(null)
    if (activeTracker?.id === tracker.id) { setView('list'); setActiveTracker(null) }
  }

  function handleLogDone() {
    loadTodayEntries(trackers)
    // Briefly show detail after logging
  }

  // ── Detail view
  if (view === 'detail' && activeTracker) {
    return (
      <TrackerDetail
        tracker={trackers.find(t => t.id === activeTracker.id) || activeTracker}
        onBack={() => { setView('list'); setActiveTracker(null); loadTodayEntries(trackers) }}
      />
    )
  }

  // ── Create view
  if (view === 'create') {
    return (
      <div>
        <button className="page-editor-back" onClick={() => setView('list')}>← Back</button>
        <CreateTrackerPanel
          onCreated={handleTrackerCreated}
          onCancel={() => setView('list')}
        />
      </div>
    )
  }

  // ── Templates view
  if (view === 'templates') {
    return (
      <TrackerTemplatePicker
        onClose={() => setView('list')}
        onCreated={tracker => { handleTrackerCreated(tracker) }}
      />
    )
  }

  // ── Log view (quick log from list)
  if (view === 'log' && activeTracker) {
    const tracker = trackers.find(t => t.id === activeTracker.id) || activeTracker
    const entries = todayEntries[tracker.id] ? [todayEntries[tracker.id]] : []

    return (
      <div>
        <button className="page-editor-back" onClick={() => { setView('list'); setActiveTracker(null) }}>← Back</button>
        <div className="tracker-detail-header" style={{ marginBottom: '1rem' }}>
          <span className="tracker-detail-icon">{tracker.icon}</span>
          <div><div className="tracker-detail-title">{tracker.title}</div></div>
        </div>
        {tracker.tracker_type === 'habit' && (
          <HabitLogPanel tracker={tracker} entries={entries} onLogged={() => { handleLogDone(); setView('list'); setActiveTracker(null) }} onUndo={() => { handleLogDone(); setView('list'); setActiveTracker(null) }} />
        )}
        {tracker.tracker_type === 'numeric' && (
          <NumericLogPanel tracker={tracker} entries={entries} onLogged={() => { handleLogDone(); setView('list'); setActiveTracker(null) }} />
        )}
        {tracker.tracker_type === 'custom' && (
          <CustomLogPanel tracker={tracker} entries={entries} onLogged={() => { handleLogDone(); setView('list'); setActiveTracker(null) }} />
        )}
      </div>
    )
  }

  // ── List view
  return (
    <div>
      <div className="ms-controls-row">
        <button className="save-btn ready" style={{ fontSize: '0.82rem', padding: '7px 16px' }} onClick={() => setView('create')}>
          + New tracker
        </button>
        <button className="ghost-btn" style={{ fontSize: '0.82rem' }} onClick={() => setView('templates')}>
          From template
        </button>
      </div>

      {!loading && trackers.length === 0 && (
        <div className="ms-empty">
          <div className="ms-empty-icon">🌿</div>
          <div className="ms-empty-title">No trackers yet</div>
          <div className="ms-empty-desc">Track habits, numbers, or anything you like.</div>
          <div className="ms-empty-actions">
            <button className="save-btn ready" onClick={() => setView('create')}>Create your first tracker</button>
            <button className="ghost-btn" onClick={() => setView('templates')}>Browse templates</button>
          </div>
        </div>
      )}

      {trackers.length > 0 && (
        <div className="ms-trackers-grid">
          {trackers.map(tracker => (
            <TrackerCard
              key={tracker.id}
              tracker={tracker}
              todayEntry={todayEntries[tracker.id]}
              onLog={t => { setActiveTracker(t); setView('log') }}
              onDetail={t => { setActiveTracker(t); setView('detail') }}
              onDelete={t => setConfirmDelete(t)}
            />
          ))}
        </div>
      )}

      {/* Delete confirmation */}
      {confirmDelete && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: 'var(--card-bg, var(--surf))', borderRadius: 14, padding: '1.5rem', maxWidth: 360, width: '100%' }}>
            <p style={{ marginBottom: '1rem', color: 'var(--ink)', fontSize: '0.95rem' }}>
              Delete <strong>"{confirmDelete.title}"</strong> and all its entries? This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="ghost-btn" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="save-btn ready" style={{ background: 'var(--bark)' }} onClick={() => handleDelete(confirmDelete)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
